import * as React from 'react';
import { cn } from '@workspace/lib';

import { typographyVariants, TypeographyVariants } from '@workspace/ui/common/Typography/typography.variants';

// Converts ^text^ patterns in string children to <sup> elements.
// Unpaired ^ characters are left as-is. Non-string children pass through unchanged.
function processChildren(children: React.ReactNode): React.ReactNode {
  if (typeof children === 'string') {
    // Fast path: skip regex entirely when no ^ is present
    if (!children.includes('^')) return children;
    // Split on paired ^...^ — captured groups land at odd indices
    const parts = children.split(/\^([^^]+)\^/);
    if (parts.length === 1) return children;
    return parts.map((part, i) => (i % 2 === 1 ? <sup key={i}>{part}</sup> : part));
  }
  if (Array.isArray(children)) {
    return children.map((child, i) =>
      typeof child === 'string' ? <React.Fragment key={i}>{processChildren(child)}</React.Fragment> : child,
    );
  }
  return children;
}

export type TypographyVariant = NonNullable<TypeographyVariants['variant']>;

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant: TypographyVariant;
  children: React.ReactNode;
  className?: string;
  href?: string;
  as?: React.ElementType;
  onClick?: (e: React.MouseEvent) => void;
  fontWeight?: string;
  textColor?: string;
  style?: React.CSSProperties;
  loading?: boolean;
  styleVariant?: TypographyVariant;
}

export function Typography({
  variant,
  children,
  className,
  href,
  as,
  onClick,
  fontWeight,
  textColor,
  style,
  loading = false,
  styleVariant,
  ...rest
}: TypographyProps) {
  const tone: TypeographyVariants['tone'] =
    variant === 'link' ? 'link' : variant === 'hyperlinkVisited' ? 'linkVisited' : 'default';

  // Use styleVariant for styling if provided, otherwise use variant
  const styleVariantToUse = styleVariant || variant;
  const classes = typographyVariants({ variant: styleVariantToUse, tone, loading });
  const merged = cn(classes, fontWeight, className);
  const commonProps = {
    onClick,
    className: merged,
    style: textColor ? { ...style, color: textColor } : style,
    href,
    ...rest,
  };

  const defaultTag: Record<TypographyVariant, React.ElementType> = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    bodyLg: 'p',
    bodyMd: 'p',
    bodySm: 'p',
    bodyXs: 'p',
    bodyTiny: 'p',
    link: href ? 'a' : 'span',
    hyperlinkVisited: href ? 'a' : 'span',
    button: 'span',
  };

  const Component = as ?? defaultTag[variant] ?? 'span';

  const processedChildren = processChildren(children);

  return <Component {...commonProps}>{processedChildren}</Component>;
}
